import { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, IconButton, FormControlLabel, Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  usePayrollRuleSets,
  usePayrollCatalogs,
  useCreateInheritedPayrollRuleSet,
  useClonePayrollRuleSet,
  useUpdatePayrollRuleSet,
  useDeletePayrollRuleSet,
  useDriverPayrollProfiles,
  useUpsertDriverPayrollProfile,
  useDrivers,
} from '../../hooks/useApi';
import type { PayrollEmploymentType } from '../../types';
import PayrollRuleSetEditor from './PayrollRuleSetEditor';

export default function PayrollSettings() {
  const { data: rulesets = [], isLoading: loadingRs } = usePayrollRuleSets();
  const { data: catalogs = [] } = usePayrollCatalogs();
  const { data: profiles = [] } = useDriverPayrollProfiles();
  const { data: drivers = [] } = useDrivers();

  const createInherit = useCreateInheritedPayrollRuleSet();
  const clone = useClonePayrollRuleSet();
  const update = useUpdatePayrollRuleSet();
  const deleteRs = useDeletePayrollRuleSet();
  const upsertProfile = useUpsertDriverPayrollProfile();

  const [newName, setNewName] = useState('ATL chauffør (standard)');
  const [profileDialog, setProfileDialog] = useState<{ open: boolean; driverId: string; driverName: string; existing?: any } | null>(null);
  const [editingRuleSet, setEditingRuleSet] = useState<any | null>(null);

  async function handleCreateInherit() {
    if (!catalogs[0]) return;
    await createInherit.mutateAsync({
      catalogKey: catalogs[0].key,
      name: newName,
      isDefault: rulesets.length === 0,
    });
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Indstillinger — løn</Typography>

      {/* Rule sets */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>Regelsæt</Typography>
          </Box>

          {loadingRs ? (
            <CircularProgress size={20} />
          ) : rulesets.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Intet regelsæt endnu. Opret et der arver fra ATL-overenskomsten.
            </Alert>
          ) : null}

          {rulesets.length === 0 && catalogs.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <TextField
                size="small"
                label="Navn"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={createInherit.isPending ? <CircularProgress size={14} /> : <AddIcon />}
                onClick={handleCreateInherit}
                disabled={createInherit.isPending}
              >
                Opret fra {catalogs[0].key}
              </Button>
            </Box>
          )}

          {rulesets.map((rs: any) => {
            // Detect stale cloned ruleset — catalog has newer validFrom rows than clone
            let staleRates: string[] = [];
            if (rs.isCloned && rs.catalogKey) {
              const cat = catalogs.find((c: any) => c.key === rs.catalogKey);
              if (cat) {
                const have = new Set((rs.rateOverrides || []).map((r: any) => String(r.validFrom).slice(0, 10)));
                staleRates = (cat.rates || [])
                  .map((r: any) => String(r.validFrom).slice(0, 10))
                  .filter((vf: string) => !have.has(vf));
              }
            }
            return (
            <Box key={rs.id} sx={{ mt: 1, p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {staleRates.length > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Nye overenskomst-satser tilgængelige ({staleRates.join(', ')}). Dit klonede regelsæt følger ikke auto-opdateringer — du skal selv indtaste nye satser eller slette klonen og opret et nyt arvende regelsæt.
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{rs.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {rs.catalogKey && !rs.isCloned && (
                      <Chip size="small" label={`Arver fra ${rs.catalogKey}`} color="success" variant="outlined" />
                    )}
                    {rs.isCloned && (
                      <Chip size="small" label="Klonet (lokalaftale)" color="warning" variant="outlined" />
                    )}
                    {rs.isDefault && <Chip size="small" label="Default" color="primary" />}
                    <Chip size="small" label={`${rs._count?.profiles || 0} chauffører`} variant="outlined" />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setEditingRuleSet(rs)}
                  >
                    Konfigurér
                  </Button>
                  {!rs.isCloned && (
                    <Button
                      size="small"
                      onClick={() => clone.mutate(rs.id)}
                      disabled={clone.isPending}
                    >
                      Klon til lokalaftale
                    </Button>
                  )}
                  {!rs.isDefault && (
                    <Button
                      size="small"
                      onClick={() => update.mutate({ id: rs.id, data: { isDefault: true } })}
                    >
                      Sæt som default
                    </Button>
                  )}
                  <IconButton size="small" color="error" onClick={() => {
                    if (confirm(`Slet regelsæt "${rs.name}"?`)) deleteRs.mutate(rs.id);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* Driver profiles */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Chaufførprofiler</Typography>
          {rulesets.length === 0 ? (
            <Alert severity="info">Opret et regelsæt først.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Chauffør</TableCell>
                    <TableCell>Ansættelse</TableCell>
                    <TableCell>Starts</TableCell>
                    <TableCell>Regelsæt</TableCell>
                    <TableCell>Ugetimer</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drivers.map((d: any) => {
                    const profile = profiles.find((p: any) => p.driverId === d.id);
                    return (
                      <TableRow key={d.id} hover>
                        <TableCell>{d.firstName} {d.lastName}</TableCell>
                        <TableCell>
                          {profile ? (
                            <Chip size="small" label={profile.employmentType === 'TIMELOENNET' ? 'Timeløn' : 'Fuldløn'} />
                          ) : (
                            <Typography variant="caption" color="text.secondary">Ikke opsat</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {profile ? new Date(profile.employmentStart).toLocaleDateString('da-DK') : '—'}
                        </TableCell>
                        <TableCell>{profile?.ruleSet?.name || '—'}</TableCell>
                        <TableCell>{profile?.weeklyContractHours || '—'}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setProfileDialog({
                              open: true,
                              driverId: d.id,
                              driverName: `${d.firstName} ${d.lastName}`,
                              existing: profile,
                            })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {profileDialog?.open && (
        <ProfileDialog
          driverId={profileDialog.driverId}
          driverName={profileDialog.driverName}
          existing={profileDialog.existing}
          rulesets={rulesets}
          onClose={() => setProfileDialog(null)}
          onSave={async (data: any) => {
            await upsertProfile.mutateAsync({ driverId: profileDialog.driverId, data });
            setProfileDialog(null);
          }}
        />
      )}

      {editingRuleSet && (
        <PayrollRuleSetEditor
          ruleSet={editingRuleSet}
          onClose={() => setEditingRuleSet(null)}
        />
      )}
    </Box>
  );
}

function ProfileDialog({
  driverId, driverName, existing, rulesets, onClose, onSave,
}: {
  driverId: string;
  driverName: string;
  existing?: any;
  rulesets: any[];
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
}) {
  const [ruleSetId, setRuleSetId] = useState(existing?.ruleSetId || rulesets[0]?.id || '');
  const [employmentType, setEmploymentType] = useState<PayrollEmploymentType>(existing?.employmentType || 'TIMELOENNET');
  const [employmentStart, setEmploymentStart] = useState(
    existing?.employmentStart ? String(existing.employmentStart).slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [weeklyContractHours, setWeeklyContractHours] = useState(String(existing?.weeklyContractHours || 37));
  const [monthlySalary, setMonthlySalary] = useState(existing?.monthlySalary ? String(existing.monthlySalary) : '');
  const [pensionOptIn, setPensionOptIn] = useState(existing?.pensionOptIn ?? true);
  const [notes, setNotes] = useState(existing?.notes || '');

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Chaufførprofil — {driverName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <FormControl size="small">
            <InputLabel>Regelsæt</InputLabel>
            <Select value={ruleSetId} label="Regelsæt" onChange={e => setRuleSetId(e.target.value)}>
              {rulesets.map((r: any) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Ansættelse</InputLabel>
            <Select value={employmentType} label="Ansættelse" onChange={e => setEmploymentType(e.target.value as PayrollEmploymentType)}>
              <MenuItem value="TIMELOENNET">Timeløn</MenuItem>
              <MenuItem value="FULDLOENNET">Fuldløn (månedsløn)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Ansættelse start"
            type="date"
            value={employmentStart}
            onChange={e => setEmploymentStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Bruges til 9-måneders anciennitetstillæg"
          />
          <TextField
            size="small"
            label="Ugentlige kontrakttimer"
            type="number"
            value={weeklyContractHours}
            onChange={e => setWeeklyContractHours(e.target.value)}
          />
          {employmentType === 'FULDLOENNET' && (
            <TextField
              size="small"
              label="Månedsløn"
              type="number"
              value={monthlySalary}
              onChange={e => setMonthlySalary(e.target.value)}
            />
          )}
          <FormControlLabel
            control={<Checkbox checked={pensionOptIn} onChange={e => setPensionOptIn(e.target.checked)} size="small" />}
            label="Pension"
          />
          <TextField
            size="small"
            label="Noter"
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button
          variant="contained"
          onClick={() => onSave({
            driverId,
            driverName,
            ruleSetId,
            employmentType,
            employmentStart,
            weeklyContractHours: parseFloat(weeklyContractHours),
            monthlySalary: monthlySalary ? parseFloat(monthlySalary) : null,
            pensionOptIn,
            notes: notes || null,
          })}
          disabled={!ruleSetId}
        >
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  );
}
